import { network } from '@root/utils'
import { DefaultQueryParams, handleReturn, MutationFunction, QueryFunction } from './root'

export type BusinessIdentifier = {
  id: number
  type: string
  value: string
}

export type BusinessIdentifierListResponse = {
  count: number
  next: string | null
  previous: string | null
  results: BusinessIdentifier[]
}

export type BusinessIdentifierTypeOption = {
  label: string
  value: string
  info: string
  required?: boolean
}

interface BusinessIdentifierListProps {
  slug: string
}

interface BusinessIdentifierInstanceProps extends BusinessIdentifierListProps {
  identifierId: number
}

interface BusinessIdentifierRequestData {
  type: string
  value: string
}

const getPathFromNext = (next: string | null): string | null => {
  if (!next) return null

  try {
    const url = new URL(next)
    return `${url.pathname}${url.search}`
  } catch (_) {
    return next
  }
}

export const fetchBusinessIdentifierTypes: QueryFunction<
  BusinessIdentifierListProps,
  BusinessIdentifierTypeOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/business_identifiers/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchBusinessIdentifiers: QueryFunction<
  BusinessIdentifierListProps,
  BusinessIdentifierListResponse
> = async ({ slug }, params: DefaultQueryParams = null) => {
  const url = `/v1/business/${slug}/identifiers/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const fetchAllBusinessIdentifiers: QueryFunction<
  BusinessIdentifierListProps,
  BusinessIdentifier[]
> = async ({ slug }) => {
  let nextPath: string | null = `/v1/business/${slug}/identifiers/`
  const results: BusinessIdentifier[] = []

  while (nextPath) {
    const { data } = await network.httpClient.request<BusinessIdentifierListResponse>({
      url: nextPath,
      method: 'GET'
    })

    results.push(...(data?.results || []))
    nextPath = getPathFromNext(data?.next || null)
  }

  return results
}

export const fetchBusinessIdentifier: QueryFunction<
  BusinessIdentifierInstanceProps,
  BusinessIdentifier
> = async ({ slug, identifierId }) => {
  const url = `/v1/business/${slug}/identifiers/${identifierId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const createBusinessIdentifier: MutationFunction<
  BusinessIdentifierListProps,
  BusinessIdentifierRequestData
> = async ({ slug }, requestData) => {
  const url = `/v1/business/${slug}/identifiers/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const updateBusinessIdentifier: MutationFunction<
  BusinessIdentifierInstanceProps,
  BusinessIdentifierRequestData
> = async ({ slug, identifierId }, requestData) => {
  const url = `/v1/business/${slug}/identifiers/${identifierId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteBusinessIdentifier: MutationFunction<
  BusinessIdentifierInstanceProps,
  BusinessIdentifier
> = async ({ slug, identifierId }) => {
  const url = `/v1/business/${slug}/identifiers/${identifierId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'DELETE',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}
